import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CancelBookingDialog from "@/components/CancelBookingDialog";
import PayBalanceDialog from "@/components/PayBalanceDialog";
import { ArrowUpRight, CalendarPlus, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
    pending: "bg-bone2 text-espresso",
    confirmed: "bg-forest text-bone",
    cancelled: "bg-terracotta/20 text-terracotta",
    completed: "bg-espresso/15 text-espresso",
};

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [payTarget, setPayTarget] = useState(null);
    const [payMethods, setPayMethods] = useState([]);

    const refresh = () => api.get("/bookings").then((r) => setBookings(r.data));
    useEffect(() => {
        api.get("/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
        api.get("/payments/methods").then((r) => setPayMethods(r.data.methods || [])).catch(() => setPayMethods([]));
    }, []);

    // Handle PayPal redirect return when checkout was initiated from the dashboard
    useEffect(() => {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("pp") === "capture") {
            const orderId = sp.get("token");
            const bookingId = sp.get("booking_id");
            if (orderId && bookingId) {
                api.post("/payments/paypal/capture", { booking_id: bookingId, order_id: orderId })
                    .then(() => { toast.success(t("dashboard.paySuccess")); refresh(); })
                    .catch((e) => toast.error(e.response?.data?.detail || t("book.paymentFailed")))
                    .finally(() => window.history.replaceState({}, "", "/dashboard"));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const today = new Date();
    const isCancelable = (b) => {
        if (!b) return false;
        if (b.status === "cancelled" || b.status === "completed") return false;
        try {
            const ci = new Date(b.check_in);
            return ci > today;
        } catch {
            return false;
        }
    };
    const canExportIcs = (b) => b && (b.status === "confirmed" || b.status === "completed");
    const canPay = (b) => b && b.status === "pending" && payMethods.length > 0;

    const exportIcs = async (b) => {
        try {
            const res = await api.get(`/bookings/${b.id}/ics`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `wild-coffee-${b.id.slice(0, 8)}.ics`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("dashboard.icsFailed"));
        }
    };

    return (
        <div data-testid="dashboard-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 lg:pt-40 pb-24 px-6 lg:px-12">
                <div className="max-w-[1300px] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                        <div className="md:col-span-8">
                            <div className="eyebrow mb-4">{t("dashboard.eyebrow")}</div>
                            <h1 className="font-chiller text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                {t("dashboard.welcome", user?.name?.split(" ")[0] || "")}
                            </h1>
                        </div>
                        <div className="md:col-span-3 md:col-start-10 self-end">
                            <Link to="/book" data-testid="dashboard-new-booking" className="inline-flex items-center gap-2 px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold">
                                {t("dashboard.newBooking")} <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </div>

                    {loading ? (
                        <div className="font-serif text-2xl text-espresso">{t("common.loading")}</div>
                    ) : bookings.length === 0 ? (
                        <div className="border border-espresso/15 p-12 text-center" data-testid="dashboard-empty">
                            <div className="eyebrow mb-3">{t("dashboard.empty.eyebrow")}</div>
                            <h2 className="font-serif text-4xl mb-4">{t("dashboard.empty.title")}</h2>
                            <p className="text-muted2 mb-6">{t("dashboard.empty.sub")}</p>
                            <Link to="/cabins" className="inline-flex items-center gap-2 text-forest hover:text-terracotta text-xs uppercase tracking-widest2 font-semibold">
                                {t("dashboard.empty.browse")} <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {bookings.map((b, i) => (
                                <div key={b.id} className="grid grid-cols-1 md:grid-cols-12 gap-6 border border-espresso/15 p-6 lg:p-8 items-start" data-testid={`booking-row-${i}`}>
                                    <div className="md:col-span-4">
                                        <div className="eyebrow mb-2">{t("dashboard.reservation")}</div>
                                        <h3 className="font-serif text-2xl leading-tight">{b.cabin_name}</h3>
                                        <div className="text-xs text-muted2 mt-2">{t("dashboard.ref")} · {b.id.slice(0, 8).toUpperCase()}</div>
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="eyebrow mb-2">{t("dashboard.dates")}</div>
                                        <div className="text-base">{b.check_in} → {b.check_out}</div>
                                        <div className="text-xs text-muted2 mt-1">{b.nights} {b.nights === 1 ? t("common.night") : t("common.nights")} · {b.guests} {t("book.guests").toLowerCase()}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="eyebrow mb-2">{t("dashboard.total")}</div>
                                        <div className="font-serif text-2xl">{formatMoney(b.total_price)}</div>
                                    </div>
                                    <div className="md:col-span-3 md:text-right">
                                        <span className={`inline-block px-3 py-1.5 text-xs uppercase tracking-widest2 font-semibold ${STATUS_COLOR[b.status] || STATUS_COLOR.pending}`}>
                                            {t(`status.${b.status}`)}
                                        </span>
                                    </div>
                                    {b.experiences && b.experiences.length > 0 && (
                                        <div className="md:col-span-12 pt-4 border-t border-espresso/10 mt-2">
                                            <div className="eyebrow text-espresso/70 mb-2">{t("dashboard.addons")}</div>
                                            <ul className="text-sm text-muted2 space-y-1">
                                                {b.experiences.map((e) => (
                                                    <li key={e.experience_id} className="flex justify-between">
                                                        <span>{e.name} × {e.quantity}</span>
                                                        <span>{formatMoney(e.price * e.quantity)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="md:col-span-12 pt-4 border-t border-espresso/10 mt-2 flex flex-wrap items-center justify-between gap-3">
                                        {b.status === "cancelled" && b.refund_amount != null ? (
                                            <div className="text-xs text-muted2" data-testid={`booking-refund-${i}`}>
                                                {t("cancel.refundLabel")}: <span className="text-ink">{formatMoney(b.refund_amount)}</span> ({b.refund_pct}%)
                                            </div>
                                        ) : <span />}
                                        <div className="flex items-center gap-5">
                                            {canPay(b) && (
                                                <button
                                                    onClick={() => setPayTarget(b)}
                                                    data-testid={`booking-pay-btn-${i}`}
                                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold"
                                                >
                                                    <CreditCard size={14} /> {t("dashboard.payBalance")}
                                                </button>
                                            )}
                                            {canExportIcs(b) && (
                                                <button
                                                    onClick={() => exportIcs(b)}
                                                    data-testid={`booking-ics-btn-${i}`}
                                                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest2 text-forest hover:text-terracotta"
                                                >
                                                    <CalendarPlus size={14} /> {t("dashboard.exportIcs")}
                                                </button>
                                            )}
                                            {isCancelable(b) && (
                                                <button
                                                    onClick={() => setCancelTarget(b)}
                                                    data-testid={`booking-cancel-btn-${i}`}
                                                    className="text-xs uppercase tracking-widest2 text-terracotta hover:text-forest"
                                                >
                                                    {t("cancel.title")}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            <CancelBookingDialog
                booking={cancelTarget}
                open={!!cancelTarget}
                onOpenChange={(o) => { if (!o) setCancelTarget(null); }}
                onCancelled={() => refresh()}
            />
            <PayBalanceDialog
                booking={payTarget}
                methods={payMethods}
                open={!!payTarget}
                onOpenChange={(o) => { if (!o) setPayTarget(null); }}
                onPaid={() => { setPayTarget(null); refresh(); }}
            />
            <Footer />
        </div>
    );
}
